/**
 * Vendor Categorization Memory — Wave D-3.
 *
 * Learns vendor → expense-account preferences directly from the posted GL.
 * Pure derivation over `JournalEntry[]` — no separate persistence layer, no
 * LLM. The "memory" is the journal itself; the longer the system runs, the
 * sharper the suggestions become.
 *
 * Read pattern:
 *   const suggestion = suggestVendorAccount(vendorId, journalEntries);
 *   // → { accountId: "5060", confidence: 0.87, sampleSize: 12, lastUsedAt: "..."}
 *
 * Use to pre-fill the GL account on a new AP bill line. The user can always
 * override; every override reshapes the next suggestion.
 */
import type { JournalEntry } from "./glTypes";

/** Accounts that should never appear as a "categorization" suggestion. */
const NON_CATEGORIZABLE = new Set<string>([
  "2000", // AP control
  "1000", // Cash — operating
  "1010", // Cash — payroll
  "1090", // Unreconciled cash
  "1100", // AR control
  "2200", // Deferred revenue
  "4990", // Purchase discounts
]);

export interface VendorAccountTally {
  accountId: string;
  count: number;
  totalAmount: number;
  lastUsedAt: string; // ISO date of most recent posting
}

export interface VendorSuggestion {
  accountId: string;
  /** 0..1 — share of vendor's debit lines that hit this account, weighted by recency. */
  confidence: number;
  /** Number of historical postings that influenced the suggestion. */
  sampleSize: number;
  lastUsedAt: string;
  /** Plain-English rationale for the UI tooltip. */
  reason: string;
}

const RECENCY_HALF_LIFE_DAYS = 120;

function recencyWeight(iso: string, asOf: number): number {
  const t = Date.parse(iso);
  if (!Number.isFinite(t)) return 0.5;
  const days = Math.max(0, (asOf - t) / 86_400_000);
  // Exponential decay with 120-day half-life: a 4-month-old entry counts half.
  return Math.pow(0.5, days / RECENCY_HALF_LIFE_DAYS);
}

/**
 * Per-vendor breakdown of every account that vendor's debits have ever hit.
 * Used to render a small "history" popover next to the suggested account.
 */
export function getVendorAccountTally(
  vendorId: string,
  entries: JournalEntry[],
): VendorAccountTally[] {
  const acc = new Map<string, VendorAccountTally>();
  for (const je of entries) {
    if (je.status !== "posted") continue;
    for (const l of je.lines) {
      if (l.vendorId !== vendorId) continue;
      if (l.side !== "debit") continue;            // categorization = where the cost lands
      if (NON_CATEGORIZABLE.has(l.accountId)) continue;
      const prev = acc.get(l.accountId);
      const postedAt = je.postingDate;
      if (prev) {
        prev.count += 1;
        prev.totalAmount += l.amount;
        if (postedAt > prev.lastUsedAt) prev.lastUsedAt = postedAt;
      } else {
        acc.set(l.accountId, {
          accountId: l.accountId,
          count: 1,
          totalAmount: l.amount,
          lastUsedAt: postedAt,
        });
      }
    }
  }
  return [...acc.values()].sort((a, b) => b.count - a.count || b.totalAmount - a.totalAmount);
}

/**
 * Suggest the most likely expense/WIP account for a vendor's next bill line.
 * Returns `null` when there is no history at all (cold start — leave the
 * UI's existing default in place).
 */
export function suggestVendorAccount(
  vendorId: string,
  entries: JournalEntry[],
  asOfIso?: string,
): VendorSuggestion | null {
  if (!vendorId) return null;
  const asOf = Date.parse(asOfIso ?? new Date().toISOString());
  const weighted = new Map<string, { weight: number; count: number; last: string }>();
  let totalWeight = 0;

  for (const je of entries) {
    if (je.status !== "posted") continue;
    for (const l of je.lines) {
      if (l.vendorId !== vendorId) continue;
      if (l.side !== "debit") continue;
      if (NON_CATEGORIZABLE.has(l.accountId)) continue;
      const w = recencyWeight(je.postingDate, asOf);
      totalWeight += w;
      const prev = weighted.get(l.accountId);
      if (prev) {
        prev.weight += w;
        prev.count += 1;
        if (je.postingDate > prev.last) prev.last = je.postingDate;
      } else {
        weighted.set(l.accountId, { weight: w, count: 1, last: je.postingDate });
      }
    }
  }
  if (totalWeight === 0 || weighted.size === 0) return null;

  let bestId = "";
  let best: { weight: number; count: number; last: string } | null = null;
  for (const [id, v] of weighted) {
    if (!best || v.weight > best.weight) { best = v; bestId = id; }
  }
  if (!best) return null;

  const confidence = Math.max(0, Math.min(1, best.weight / totalWeight));
  const sampleSize = [...weighted.values()].reduce((s, v) => s + v.count, 0);

  const reason =
    sampleSize === 1
      ? `Only 1 prior posting — low-confidence guess.`
      : confidence >= 0.85
        ? `${(confidence * 100).toFixed(0)}% of this vendor's ${sampleSize} prior debits hit this account.`
        : `Most frequent of ${weighted.size} accounts used for this vendor (${sampleSize} postings).`;

  return {
    accountId: bestId,
    confidence,
    sampleSize,
    lastUsedAt: best.last,
    reason,
  };
}
